import {assertAndContinue} from 'Asserts';
import {COMPLEX_VIEW_NAMES, USER_DEFINED_VIEW_SCHEMA} from 'insights/warehouse/AnalyticsWarehouseConstants';

import {WarehouseColumnData} from './warehouseModelsDataLayout';

/**
 * Return schema name and complete tablename of input name.
 *
 * If an input tablename corresponds to the name of a Complex Warehouse View,
 * then it will NOT be suffixed with $raw because its name is correct as is.
 * If an input tablename doesn't correspond to the name of a Complex Warehouse View and it is not
 * already suffixed with $raw, this function will append $raw, as it is assumed that the complete
 * name is the name of the associated, $raw table.
 * Examples:
 *  - benchling.user -> [benchling, user$raw]
 *  - benchling.user$raw -> [benchling, user$raw]
 *  - benchling.cool_complex_view -> [benchling, cool_complex_view] where cool_complex_view is the name
 * of a Complex Warehouse View
 */
export function schemaQualifiedTableNameToSchemaAndCompleteTableNames(
  schemaQualifiedTableName: string
): [string, string] {
  // schemaQualifiedTableName.split should always return two elements because both schema names and warehouse table names
  // are generated from name_to_sql_identifier, which removes periods. Therefore, schemaQualifiedTableName
  // is guaranteed to have only one period.
  const result = schemaQualifiedTableName.split('.');
  assertAndContinue(result.length === 2, 'Invalid schema-qualified table name', {
    schemaQualifiedTableName,
  });
  const [schemaName, tableName] = result;
  if (schemaName === USER_DEFINED_VIEW_SCHEMA) {
    return [schemaName, tableName];
  }
  if (COMPLEX_VIEW_NAMES.has(tableName)) {
    return [schemaName, tableName];
  }
  return [schemaName, tableName.endsWith('$raw') ? tableName : `${tableName}$raw`];
}

/**
 * Given a warehouse table name without its schemaName/org, return its sqlIdentifier as specified in the
 * Benchling schema that defined the table.
 *
 * Examples:
 * tableName -> tableName
 * tableName$raw -> tableName
 * tableName$raw$raw -> tableName$raw
 */
export function getSQLIdentifierFromTableName(tableName: string): string | null {
  // Removes the last "$raw" suffix if it exists
  const tableNameRegex = /(\S+?)(?:\$raw)?$/g;
  const tableNameRegexResult = tableNameRegex.exec(tableName);
  return tableNameRegexResult ? tableNameRegexResult[1] : null;
}

export function getTableViewName(schemaName: string, rawTableName: string): string {
  return `${schemaName}.${rawTableName.replace(/\$raw$/, '')}`;
}

/**
 * Check that two columns are logically equal.
 *
 * This function is needed instead of using isEqual because WarehouseColumnData contains circular references:
 * column.relationships[0].source == column
 */
// TODO(BNCH-29085): Abstract to shared helper file.
export function areColumnsEqual(column1: WarehouseColumnData, column2: WarehouseColumnData): boolean {
  return (
    column1.name === column2.name &&
    column1.table.name === column2.table.name &&
    column1.table.schemaName === column2.table.schemaName
  );
}
